<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
// custom: T4a — resolve Haikunator / telefone-disfarçado / pushname antes de exibir
import { resolveContactName } from 'dashboard/composables/cw_custom/resolveContactName';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
// custom: T7 PR4 — botão ★ no header da conversa aberta
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
// custom (equipe interna): botão "voltar" scope-aware — sem isso volta para home (clientes)
import { internalScopeForRoute } from 'dashboard/composables/cw_custom/internalScope';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

// custom (conversas 2026-06-23): o painel de contato/CRM (ContactPanel, que hospeda o
// bloco Kanban do CRM dentro da conversa) deixou de ter o ícone de pessoa flutuante
// (SidepanelSwitch). Agora abre/fecha clicando no título do header (avatar + nome).
const { uiSettings, updateUISettings } = useUISettings();
const toggleContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: !uiSettings.value.is_contact_sidebar_open,
    is_copilot_panel_open: false,
  });
};

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  // custom (equipe interna): propaga o escopo para que "voltar" retorne a /equipe e não a
  // /dashboard (home = clientes). crm_internal_inbox_conversation não está no conversationTypeMap,
  // inbox_id/label/team são undefined — sem o escopo, backButtonUrl cairia no branch padrão
  // (dashboard) e jogaria o usuário em "Conversas" em vez da "Caixa de Entrada" interna.
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
    internalScope: internalScopeForRoute(name),
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

// custom: T4a — nome resolvido: nome real > pushname WhatsApp > telefone > fallback
const resolvedContactName = computed(() =>
  resolveContactName(currentContact.value, t('CONVERSATION.UNKNOWN_CONTACT'))
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

// custom: T7 PR4 — ★ favorita no cabeçalho da conversa aberta.
// Usa o mesmo módulo Vuex (cwCustomConversationFavorites) do PR3 — mesmo Set, mesma
// reatividade. Ao favoritar no header, a aba Favoritas e o card na lista refletem
// imediatamente (toggle otimista no módulo).
// fallback para Set vazio: em testes que montam o header sem registrar o módulo
// cwCustomConversationFavorites, o getter devolve undefined → crash no .has().
// Em produção o módulo está em main (PR3); undefined indica store incompleto.
const favoriteIds = computed(
  () =>
    store.getters['cwCustomConversationFavorites/getFavoriteIds'] || new Set()
);
const isFavorited = computed(() => favoriteIds.value.has(currentChat.value.id));

const toggleFavorite = () => {
  store.dispatch('cwCustomConversationFavorites/toggle', {
    accountId: accountId.value,
    conversationId: currentChat.value.id,
  });
};

// custom: T7 PR4 — edge: deep-link direto numa conversa (/accounts/5/conversations/42).
// Nesse fluxo o ChatList pode não ter montado ainda → getFavoriteIds retorna Set vazio
// → ★ aparece como não-favoritada incorretamente. Load aqui é idempotente: se o ChatList
// já carregou, o SET_FAVORITE_IDS substitui pelo mesmo array (sem flip visual).
onMounted(() => {
  store.dispatch('cwCustomConversationFavorites/load', accountId.value);
});
</script>

<template>
  <!-- custom (redesign Modeloja): `relative z-20` no cabeçalho.
       O menu dos três pontinhos abria CORTADO — as bolhas de mensagem pintavam por cima
       dele. O `z-50` do DropdownMenu estava certo e não adiantava nada, e a razão é sutil:
       o `backdrop-blur-[10px]` desta div (o vidro do cabeçalho) CRIA UM CONTEXTO DE
       EMPILHAMENTO. Todo z-index dos filhos passa a ser interno a ele, e o contexto inteiro
       entrava na página com `z-index: auto` — ou seja, empatado com o conteúdo e perdendo
       no critério de desempate, que é a ordem do DOM: a lista de mensagens vem depois.
       Dar um z-index ao PRÓPRIO contexto é o que resolve; mexer no filho nunca resolveria.
       Mais uma armadilha de `backdrop-filter` para a coleção — ver a memória do projeto. -->
  <div
    ref="conversationHeader"
    class="relative z-20 flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12 border-b border-n-weak bg-n-background/80 backdrop-blur-[10px]"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <!-- custom (conversas 2026-06-23): avatar + nome viram o gatilho do painel de
           contato/CRM (substitui o ícone de pessoa removido do SidepanelSwitch). Clicar
           ou Enter/Espaço abre/fecha o ContactPanel. O #id (copiar) tem @click.stop. -->
      <div
        v-tooltip.bottom-start="$t('CONVERSATION.SIDEBAR.CONTACT')"
        role="button"
        tabindex="0"
        class="flex items-center min-w-0 cursor-pointer rounded-md -mx-1 px-1 py-0.5 transition-colors hover:bg-n-alpha-1"
        @click="toggleContactPanel"
        @keydown.enter.prevent="toggleContactPanel"
        @keydown.space.prevent="toggleContactPanel"
      >
        <Avatar
          :name="resolvedContactName"
          :src="currentContact.thumbnail"
          :identity-key="
            currentContact.phone_number ||
            currentContact.identifier ||
            String(currentContact.id || '')
          "
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
          rounded-full
        />
        <div
          class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
        >
          <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
            <span
              class="text-[15px] font-semibold tracking-[-0.01em] truncate leading-tight text-n-slate-12"
            >
              {{ resolvedContactName }}
              <!-- custom: T4a — nome resolvido substitui currentContact.name no título -->
            </span>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
              icon="warning"
            />
          </div>

          <div
            class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
          >
            <button
              type="button"
              class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
              @click.stop="copyConversationId"
            >
              {{ `#${chat.id}` }}
            </button>
            <span v-if="hasMultipleInboxes">•</span>
            <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
            <span v-if="isSnoozed">•</span>
            <span v-if="isSnoozed" class="font-medium text-n-amber-10">
              {{ snoozedDisplayText }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <!-- custom: T7 PR4 — botão ★ no header; mesmo toggle do card na lista (PR3).
           Usar ButtonV4 com icon slot SVG inline para consistência com o header (lucide).
           tooltip e aria-label refletem o estado atual. -->
      <ButtonV4
        v-tooltip="
          isFavorited
            ? $t('CHAT_LIST.FAVORITE.REMOVE')
            : $t('CHAT_LIST.FAVORITE.ADD')
        "
        size="sm"
        variant="ghost"
        color="slate"
        :aria-label="
          isFavorited
            ? $t('CHAT_LIST.FAVORITE.REMOVE')
            : $t('CHAT_LIST.FAVORITE.ADD')
        "
        class="rounded-full border border-n-weak bg-white hover:!bg-n-solid-2 active:scale-[.94] transition-transform"
        :class="{ 'text-n-amber-9 hover:text-n-amber-9': isFavorited }"
        @click="toggleFavorite"
      >
        <template #default>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="size-4"
            :fill="isFavorited ? 'currentColor' : 'none'"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polygon
              points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
            />
          </svg>
        </template>
      </ButtonV4>
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
